/**
 * News/NewsPublic.jsx
 * ════════════════════════════════════════════════════════════════
 * Публічний список новин + детальний перегляд.
 * Спільний для порталу і садочка.
 *
 * Props:
 *   scope {string}  — 'portal' | 'kg'
 *   kgId  {string?} — обов'язково для scope='kg'
 * ════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faThumbtack, faCalendar, faImage, faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import { constr_uri } from '../../../../../services/apiClient';
import { newsAPI, newsCategoriesAPI } from './newsAPI';
import NewsDetail, { formatNewsDate as formatDate } from './NewsDetail';

// ── Стилі ─────────────────────────────────────────────────────────────────────

var STYLES_INJECTED = false;
function injectStyles() {
    if (STYLES_INJECTED) return;
    STYLES_INJECTED = true;
    var s = document.createElement('style');
    s.textContent = [
        '.np-card { transition: box-shadow 0.2s, transform 0.2s; cursor: pointer; }',
        '.np-card:hover { box-shadow: 0 8px 24px rgba(0,0,0,0.1); transform: translateY(-2px); }',
        '.np-body p { margin: 0 0 12px; }',
        '.np-body h3 { font-size: 17px; font-weight: 700; margin: 18px 0 8px; color: #111827; }',
        '.np-body ul, .np-body ol { padding-left: 24px; margin: 10px 0; }',
        '.np-body li { margin-bottom: 6px; }',
        '.np-overlay { position: fixed; inset: 0; z-index: 9999; background: rgba(0,0,0,0); transition: background 0.3s; pointer-events: none; }',
        '.np-overlay.visible { background: rgba(0,0,0,0.6); pointer-events: all; }',
    ].join('\n');
    document.head.appendChild(s);
}

// ── Картка новини ─────────────────────────────────────────────────────────────

var NewsCard = function({ item, onClick }) {
    var coverSrc = item.coverDocId && item.coverFileId
        ? constr_uri('docfile?doc_id=' + item.coverDocId + '&file_id=' + item.coverFileId)
        : null;

    return (
        <div
            className="np-card"
            onClick={function() { onClick(item._id); }}
            style={{
                background: '#fff', borderRadius: 14, overflow: 'hidden',
                border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                display: 'flex', flexDirection: 'column',
            }}
        >
            {/* Обкладинка */}
            <div style={{ width: '100%', height: 160, background: '#f3f4f6', position: 'relative', flexShrink: 0 }}>
                {coverSrc ? (
                    <img src={coverSrc} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FontAwesomeIcon icon={faImage} style={{ fontSize: 32, color: '#d1d5db' }} />
                    </div>
                )}
                {item.isPinned && (
                    <div style={{
                        position: 'absolute', top: 10, left: 10,
                        background: 'rgba(0,0,0,0.55)', color: '#fff',
                        width: 28, height: 28, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <FontAwesomeIcon icon={faThumbtack} style={{ fontSize: 12 }} />
                    </div>
                )}
                {item.categoryName && (
                    <div style={{
                        position: 'absolute', bottom: 10, left: 10,
                        background: item.categoryColor || '#0069ff', color: '#fff',
                        padding: '3px 10px', borderRadius: 12,
                        fontSize: 11, fontWeight: 700,
                    }}>
                        {item.categoryName}
                    </div>
                )}
            </div>

            {/* Текст */}
            <div style={{ padding: '16px 18px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <FontAwesomeIcon icon={faCalendar} style={{ fontSize: 10 }} />
                    {formatDate(item.publishAt)}
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', lineHeight: 1.35, marginBottom: 8 }}>
                    {item.title}
                </div>
                {item.excerpt && (
                    <div style={{
                        fontSize: 13, color: '#6b7280', lineHeight: 1.6,
                        overflow: 'hidden', display: '-webkit-box',
                        WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                        flex: 1,
                    }}>
                        {item.excerpt}
                    </div>
                )}
                {item.tags && item.tags.length > 0 && (
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 10 }}>
                        {item.tags.slice(0, 3).map(function(t) {
                            return (
                                <span key={t} style={{ fontSize: 11, color: '#9ca3af' }}>#{t}</span>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

// ════════════════════════════════════════════════════════════════
// ГОЛОВНИЙ КОМПОНЕНТ
// ════════════════════════════════════════════════════════════════

var NewsPublic = function({ scope, kgId }) {
    var [items, setItems]         = useState([]);
    var [page, setPage]           = useState(1);
    var [hasMore, setHasMore]     = useState(false);
    var [loading, setLoading]     = useState(true);
    var [loadingMore, setLoadingMore] = useState(false);
    var [activeCategory, setActiveCategory] = useState('');
    var [categories, setCategories] = useState([]);
    var [openId, setOpenId]       = useState(null);

    var limit = 9;

    useEffect(function() { injectStyles(); }, []);

    useEffect(function() {
        newsCategoriesAPI.list('active').then(setCategories).catch(function() {});
    }, []);

    var load = async function(p, append) {
        if (append) setLoadingMore(true); else setLoading(true);
        try {
            var result = await newsAPI.getPublicList(scope, kgId, {
                category: activeCategory, page: p, limit: limit,
            });
            setItems(function(prev) { return append ? prev.concat(result.items || []) : (result.items || []); });
            setHasMore(!!result.hasMore);
            setPage(p);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    useEffect(function() { load(1, false); }, [activeCategory, scope, kgId]);

    if (loading) {
        return <div className="text-center py-5"><Spinner animation="border" variant="primary" size="sm" /></div>;
    }

    if (items.length === 0 && !activeCategory) return null;

    return (
        <div>
            {/* Фільтр категорій */}
            {categories.length > 0 && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
                    <button
                        onClick={function() { setActiveCategory(''); }}
                        style={{
                            padding: '5px 14px', borderRadius: 16, fontSize: 12, fontWeight: 600,
                            border: '1px solid ' + (!activeCategory ? '#0069ff' : '#e5e7eb'),
                            background: !activeCategory ? '#0069ff' : '#fff',
                            color: !activeCategory ? '#fff' : '#6b7280',
                            cursor: 'pointer',
                        }}
                    >
                        Всі
                    </button>
                    {categories.map(function(c) {
                        var isActive = activeCategory === c._id;
                        return (
                            <button
                                key={c._id}
                                onClick={function() { setActiveCategory(c._id); }}
                                style={{
                                    padding: '5px 14px', borderRadius: 16, fontSize: 12, fontWeight: 600,
                                    border: '1px solid ' + (isActive ? c.color : '#e5e7eb'),
                                    background: isActive ? c.color : '#fff',
                                    color: isActive ? '#fff' : '#6b7280',
                                    cursor: 'pointer',
                                }}
                            >
                                {c.name}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Сітка карток */}
            {items.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#adb5bd' }}>Новин у цій категорії немає</div>
            ) : (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                    gap: 18,
                }}>
                    {items.map(function(item) {
                        return <NewsCard key={item._id} item={item} onClick={setOpenId} />;
                    })}
                </div>
            )}

            {/* Показати ще */}
            {hasMore && (
                <div style={{ textAlign: 'center', marginTop: 28 }}>
                    <button
                        onClick={function() { load(page + 1, true); }}
                        disabled={loadingMore}
                        style={{
                            padding: '10px 28px', borderRadius: 10, fontSize: 14, fontWeight: 600,
                            border: '1px solid #0069ff', background: '#fff', color: '#0069ff',
                            cursor: loadingMore ? 'wait' : 'pointer',
                            display: 'inline-flex', alignItems: 'center', gap: 8,
                        }}
                    >
                        {loadingMore ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faChevronDown} />}
                        Показати ще
                    </button>
                </div>
            )}

            {/* Детальний перегляд */}
            {openId && (
                <NewsDetail scope={scope} kgId={kgId} newsId={openId} onClose={function() { setOpenId(null); }} />
            )}
        </div>
    );
};

export default NewsPublic;